// Smart Daara - API Client
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests + cache-busting for GETs (prevents stale reads through CDN/proxies)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if ((config.method || 'get').toLowerCase() === 'get') {
    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';
    config.params = { ...(config.params || {}), _t: Date.now() };
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Tenant APIs
export const tenantAPI = {
  list: (params) => api.get('/tenants', { params }),
  get: (id) => api.get(`/tenants/${id}`),
  getBySlug: (slug) => api.get(`/tenants/by-slug/${slug}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  updateSubscription: (id, data) => api.put(`/tenants/${id}/subscription`, data),
  getStats: (id) => api.get(`/tenants/${id}/stats`),
  delete: (id) => api.delete(`/tenants/${id}`),
  activate: (id) => api.put(`/tenants/${id}/activate`),
  importProgramme: (id) => api.post(`/tenants/${id}/import-programme`),
  deletePermanently: (id, confirm) => api.delete(`/tenants/${id}/permanent`, { params: { confirm } }),
};

// User APIs
export const userAPI = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  // Students
  listStudents: (params) => api.get('/users/students', { params }),
  createStudent: (data) => api.post('/users/students', data),
  updateStudent: (id, data) => api.put(`/users/students/${id}`, data),
  deleteStudent: (id) => api.delete(`/users/students/${id}`),
  // Password reset (admin can reset any user in tenant; super admin can reset anyone)
  resetPassword: (userId) => api.post(`/users/${userId}/reset-password`),
  // Bulk CSV import (students)
  downloadImportTemplate: () => api.get('/users/students/import/template', { responseType: 'blob' }),
  previewStudentImport: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/users/students/import/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  commitStudentImport: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/users/students/import/commit', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Bulk CSV import (teachers)
  downloadTeacherImportTemplate: () => api.get('/users/teachers/import/template', { responseType: 'blob' }),
  previewTeacherImport: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/users/teachers/import/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  commitTeacherImport: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/users/teachers/import/commit', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Teachers
  listTeachers: (params) => api.get('/users/teachers', { params }),
  createTeacher: (data) => api.post('/users/teachers', data),
  updateTeacher: (id, data) => api.put(`/users/teachers/${id}`, data),
  deleteTeacher: (id) => api.delete(`/users/teachers/${id}`),
  assignTeacherCourses: (teacherId, courseIds) => api.put(`/users/teachers/${teacherId}/courses`, courseIds),
  // Parents
  listParents: (params) => api.get('/users/parents', { params }),
  getMyChildren: () => api.get('/users/my-children'),
  createParent: (data) => api.post('/users/parents', data),
  updateParent: (id, data) => api.put(`/users/parents/${id}`, data),
  deleteParent: (id) => api.delete(`/users/parents/${id}`),
  linkParentChildren: (parentId, childIds) => api.put(`/users/parents/${parentId}/children`, childIds),
};

// Course APIs
export const courseAPI = {
  // Classes
  listClasses: (params) => api.get('/courses/classes', { params }),
  getClass: (id) => api.get(`/courses/classes/${id}`),
  createClass: (data) => api.post('/courses/classes', data),
  updateClass: (id, data) => api.put(`/courses/classes/${id}`, data),
  deleteClass: (id) => api.delete(`/courses/classes/${id}`),
  // Subjects
  listSubjects: (params) => api.get('/courses/subjects', { params }),
  getSubject: (id) => api.get(`/courses/subjects/${id}`),
  createSubject: (data) => api.post('/courses/subjects', data),
  updateSubject: (id, data) => api.put(`/courses/subjects/${id}`, data),
  assignSubjectTeachers: (subjectId, teacherIds) => api.post(`/courses/subjects/${subjectId}/teachers`, { teacher_ids: teacherIds }),
  enrollClassInSubject: (subjectId) => api.post(`/courses/subjects/${subjectId}/enroll-class`),
  enrollStudentsInSubject: (subjectId, studentIds) => api.post(`/courses/subjects/${subjectId}/enroll-students`, { student_ids: studentIds }),
  // Courses
  listCourses: (params) => api.get('/courses/courses', { params }),
  getCourse: (id) => api.get(`/courses/courses/${id}`),
  createCourse: (data) => api.post('/courses/courses', data),
  updateCourse: (id, data) => api.put(`/courses/courses/${id}`, data),
  deleteCourse: (id) => api.delete(`/courses/courses/${id}`),
  assignTeachers: (courseId, teacherIds) => api.put(`/courses/courses/${courseId}/teachers`, teacherIds),
  // Lessons
  listLessons: (courseId) => api.get('/courses/lessons', { params: { course_id: courseId } }),
  getLesson: (id) => api.get(`/courses/lessons/${id}`),
  createLesson: (data) => api.post('/courses/lessons', data),
  updateLesson: (id, data) => api.put(`/courses/lessons/${id}`, data),
  deleteLesson: (id) => api.delete(`/courses/lessons/${id}`),
  renumberLessons: (courseId) => api.post(`/courses/courses/${courseId}/renumber-lessons`),
  reorderLessons: (courseId, lessonIds) => api.post(`/courses/courses/${courseId}/reorder-lessons`, { lesson_ids: lessonIds }),
};

// Progress APIs
export const progressAPI = {
  startLesson: (lessonId) => api.post(`/progress/lessons/${lessonId}/start`),
  updateLessonProgress: (lessonId, data) => api.put(`/progress/lessons/${lessonId}/update`, null, { params: data }),
  getLessonProgress: (lessonId) => api.get(`/progress/lessons/${lessonId}`),
  getCourseProgress: (courseId) => api.get(`/progress/courses/${courseId}`),
  getCourseLessonsProgress: (courseId) => api.get(`/progress/courses/${courseId}/lessons`),
  enrollInCourse: (courseId) => api.post(`/progress/courses/${courseId}/enroll`),
  markCourseStarted: (courseId) => api.post(`/progress/courses/${courseId}/mark-started`),
  restartCourse: (courseId) => api.post(`/progress/courses/${courseId}/restart`),
  getMyCourses: () => api.get('/progress/my-courses'),
  getChildProgress: (studentId) => api.get(`/progress/children/${studentId}`),
};

// Subscription APIs
export const subscriptionAPI = {
  listPlans: (params) => api.get('/subscriptions/plans', { params }),
  getPlan: (planType) => api.get(`/subscriptions/plans/${planType}`),
  updatePlan: (planType, data) => api.put(`/subscriptions/plans/${planType}`, null, { params: data }),
  getTenantSubscription: (tenantId) => api.get(`/subscriptions/tenant/${tenantId}`),
  getTenantLimits: (tenantId) => api.get(`/subscriptions/tenant/${tenantId}/limits`),
  createSubscription: (tenantId, data) => api.post(`/subscriptions/tenant/${tenantId}/subscribe`, null, { params: data }),
  listAll: (params) => api.get('/subscriptions', { params }),
  activate: (id) => api.put(`/subscriptions/${id}/activate`),
  cancel: (id) => api.put(`/subscriptions/${id}/cancel`),
};

// Payment APIs
export const paymentAPI = {
  createInvoice: (data) => api.post('/payments/create-invoice', null, { params: data }),
  getStatus: (paymentId) => api.get(`/payments/status/${paymentId}`),
  getTenantPayments: (tenantId) => api.get(`/payments/tenant/${tenantId}`),
  createPayout: (data) => api.post('/payments/payout', null, { params: data }),
  getBalance: (provider) => api.get('/payments/admin/balance', { params: { provider } }),
  listOmTransactions: (params) => api.get('/payments/admin/transactions', { params }),
};

// Physical school APIs
export const physicalAPI = {
  dashboard: () => api.get('/physical/academic/dashboard'),
  listYears: () => api.get('/physical/academic/years'),
  createYear: (data) => api.post('/physical/academic/years', data),
  updateYear: (id, data) => api.put(`/physical/academic/years/${id}`, data),
  listPeriods: (yearId) => api.get(`/physical/academic/years/${yearId}/periods`),
  updatePeriod: (id, data) => api.put(`/physical/academic/periods/${id}`, data),
  listLevels: () => api.get('/physical/academic/levels'),
  createLevel: (data) => api.post('/physical/academic/levels', data),
  deleteLevel: (id) => api.delete(`/physical/academic/levels/${id}`),
  listRooms: () => api.get('/physical/academic/rooms'),
  createRoom: (data) => api.post('/physical/academic/rooms', data),
  deleteRoom: (id) => api.delete(`/physical/academic/rooms/${id}`),
  listSubjects: () => api.get('/physical/academic/subjects'),
  createSubject: (data) => api.post('/physical/academic/subjects', data),
  deleteSubject: (id) => api.delete(`/physical/academic/subjects/${id}`),
  listLevelSubjects: (levelId) => api.get('/physical/academic/level-subjects', { params: { level_id: levelId } }),
  setLevelSubject: (data) => api.post('/physical/academic/level-subjects', data),
  deleteLevelSubject: (id) => api.delete(`/physical/academic/level-subjects/${id}`),
  listClasses: () => api.get('/physical/academic/classes'),
  createClass: (data) => api.post('/physical/academic/classes', data),
  updateClass: (id, data) => api.put(`/physical/academic/classes/${id}`, data),
  deleteClass: (id) => api.delete(`/physical/academic/classes/${id}`),
  getGradingConfig: () => api.get('/physical/academic/grading-config'),
  updateGradingConfig: (data) => api.put('/physical/academic/grading-config', data),
  getTimeslotConfig: () => api.get('/physical/academic/timeslot-config'),
  updateTimeslotConfig: (data) => api.put('/physical/academic/timeslot-config', data),
  listStudents: (classId) => api.get('/physical/people/students', { params: classId ? { class_id: classId } : {} }),
  createStudent: (data) => api.post('/physical/people/students', data),
  updateStudent: (userId, data) => api.put(`/physical/people/students/${userId}`, data),
  importStudentsCsv: (formData) => api.post('/physical/people/students/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  listTeachers: () => api.get('/physical/people/teachers'),
  createTeacher: (data) => api.post('/physical/people/teachers', data),
  updateTeacher: (userId, data) => api.put(`/physical/people/teachers/${userId}`, data),
  listAssignments: () => api.get('/physical/people/assignments'),
  createAssignment: (data) => api.post('/physical/people/assignments', data),
  deleteAssignment: (id) => api.delete(`/physical/people/assignments/${id}`),
  autoAssign: (apply) => api.post('/physical/people/assignments/auto', null, { params: { apply } }),
  myAssignments: () => api.get('/physical/people/my-assignments'),
  timetableEntries: (params) => api.get('/physical/timetable/entries', { params }),
  myTimetable: () => api.get('/physical/timetable/my'),
  childTimetable: (studentUserId) => api.get(`/physical/timetable/child/${studentUserId}`),
  createEntry: (data, force = false) => api.post('/physical/timetable/entries', data, { params: { force } }),
  updateEntry: (id, data, force = false) => api.put(`/physical/timetable/entries/${id}`, data, { params: { force } }),
  deleteEntry: (id) => api.delete(`/physical/timetable/entries/${id}`),
  generateTimetable: (classId, apply) => api.post('/physical/timetable/generate', null, { params: { class_id: classId || undefined, apply } }),
  clearTimetable: (classId) => api.post('/physical/timetable/clear', null, { params: { class_id: classId || undefined } }),
};

// Physical grades & report cards APIs
export const gradesAPI = {
  createEvaluation: (data) => api.post('/physical/grades/evaluations', data),
  listEvaluations: (params) => api.get('/physical/grades/evaluations', { params }),
  deleteEvaluation: (id) => api.delete(`/physical/grades/evaluations/${id}`),
  bulkGrades: (data) => api.put('/physical/grades/bulk', data),
  gradesByEvaluation: (evaluationId) => api.get('/physical/grades/by-evaluation', { params: { evaluation_id: evaluationId } }),
  setAppreciation: (data) => api.put('/physical/grades/appreciations', data),
  classSummary: (classId, periodId) => api.get('/physical/grades/class-summary', { params: { class_id: classId, period_id: periodId } }),
  setReportCard: (data) => api.put('/physical/grades/report-cards', data),
  submitReports: (classId, periodId) => api.post('/physical/grades/report-cards/submit', null, { params: { class_id: classId, period_id: periodId } }),
  publishReports: (classId, periodId) => api.post('/physical/grades/report-cards/publish', null, { params: { class_id: classId, period_id: periodId } }),
  myReportCards: () => api.get('/physical/grades/report-cards/my'),
  audit: () => api.get('/physical/grades/audit'),
  reportCardPdf: (classId, periodId, studentUserId, lang) =>
    api.get('/physical/grades/report-cards/pdf', { params: { class_id: classId, period_id: periodId, student_user_id: studentUserId, lang }, responseType: 'blob' }),
  classReportPdf: (classId, periodId, lang) =>
    api.get('/physical/grades/report-cards/class-pdf', { params: { class_id: classId, period_id: periodId, lang }, responseType: 'blob' }),
};

// Virtual transcripts APIs
export const reportsAPI = {
  transcript: (studentId, lang) => api.get('/reports/transcript', { params: { student_id: studentId || undefined, lang } }),
  transcriptPdf: (studentId, lang) => api.get('/reports/transcript/pdf', { params: { student_id: studentId || undefined, lang }, responseType: 'blob' }),
  getSettings: () => api.get('/reports/settings'),
  updateSettings: (data) => api.put('/reports/settings', data),
};

// AI (Gemini) APIs
export const aiAPI = {
  status: () => api.get('/ai/status'),
  getGlobal: () => api.get('/ai/admin/global'),
  updateGlobal: (data) => api.put('/ai/admin/global', data),
  updateSchoolBySuper: (tenantId, data) => api.put(`/ai/admin/school/${tenantId}`, data),
  getSchool: () => api.get('/ai/admin/school'),
  updateSchool: (data) => api.put('/ai/admin/school', data),
  analyzeCourse: (courseId, force) => api.post(`/ai/courses/${courseId}/analyze`, null, { params: { force } }),
  courseContent: (courseId) => api.get(`/ai/courses/${courseId}/content`),
  generateQuiz: (data) => api.post('/ai/quizzes/generate', data),
  saveQuiz: (data) => api.post('/ai/quizzes/save', data),
  generateSheet: (data) => api.post('/ai/sheets/generate', data),
  updateSheet: (courseId, data) => api.put(`/ai/sheets/${courseId}`, data),
  getSheet: (courseId) => api.get(`/ai/sheets/${courseId}`),
  sheetPdf: (courseId) => api.get(`/ai/sheets/${courseId}/pdf`, { responseType: 'blob' }),
  tutorAsk: (courseId, data) => api.post(`/ai/tutor/${courseId}`, data),
  tutorHistory: (courseId) => api.get(`/ai/tutor/${courseId}/history`),
};

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Notification APIs
export const notificationAPI = {
  send: (data) => api.post('/notifications/send', null, { params: data }),
  getMy: (params) => api.get('/notifications/my', { params }),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Analytics APIs
export const analyticsAPI = {
  getTeacherPerformance: () => api.get('/analytics/teachers'),
};

// Enrollment APIs
export const enrollmentAPI = {
  // Student endpoints
  getMyCourses: () => api.get('/enrollments/my-courses'),
  getAvailableCourses: () => api.get('/enrollments/available-courses'),
  requestEnrollment: (courseId) => api.post('/enrollments/request', { course_id: courseId }),
  getMyRequests: () => api.get('/enrollments/my-requests'),
  // Educator endpoints
  getPendingEnrollments: () => api.get('/enrollments/pending'),
  processEnrollment: (enrollmentId, action) => api.post('/enrollments/action', { enrollment_id: enrollmentId, action }),
  enrollStudent: (courseId, userId) => api.post('/enrollments/enroll-student', null, { params: { course_id: courseId, user_id: userId } }),
  getCourseStudents: (courseId) => api.get(`/enrollments/course/${courseId}/students`),
  removeEnrollment: (enrollmentId) => api.delete(`/enrollments/${enrollmentId}`),
};

// Quiz APIs
export const quizAPI = {
  // Educator endpoints
  createQuiz: (data) => api.post('/quizzes', data),
  getQuiz: (quizId) => api.get(`/quizzes/${quizId}`),
  updateQuiz: (quizId, data) => api.put(`/quizzes/${quizId}`, data),
  deleteQuiz: (quizId) => api.delete(`/quizzes/${quizId}`),
  listQuizzes: (courseId) => api.get(`/quizzes/course/${courseId}`),
  addQuestion: (quizId, data) => api.post(`/quizzes/${quizId}/questions`, data),
  listQuestions: (quizId) => api.get(`/quizzes/${quizId}/questions`),
  updateQuestion: (questionId, data) => api.put(`/quizzes/questions/${questionId}`, data),
  deleteQuestion: (questionId) => api.delete(`/quizzes/questions/${questionId}`),
  // Student endpoints
  startQuiz: (quizId) => api.post(`/quizzes/${quizId}/start`),
  submitAnswer: (attemptId, questionId, answer) => api.post(`/quizzes/attempts/${attemptId}/answer`, { question_id: questionId, answer }),
  completeQuiz: (attemptId) => api.post(`/quizzes/attempts/${attemptId}/complete`),
  getMyAttempts: (quizId) => api.get(`/quizzes/attempts/my-history/${quizId}`),
  checkQuizStatus: (courseId) => api.get(`/quizzes/course/${courseId}/has-passed`),
};

export default api;
